import { format } from "date-fns";
import { useMemo, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { defaultButtonClasses } from "@/constants/ui";
import { isMemberAnAdmin, isMemberAPracticeManager, isSystemAccount } from "@/core/auth";
import { useUpdateBookingDateTime } from "@/hooks/useUpdateBookingDateTime";
import { isBookingWithin24Hours } from "@/utils/bookingWithin24Hours";
import { AlertCircle, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { useMember } from "@/contexts/memberContext";
import { useChangeBookingFlow } from "./ChangeBookingFlowProvider";
import { endTimeToMinutes } from "@/utils/bookingTime";

type DurationConfirmationProps = {
  onClose?: () => void;
  newStartTime: string;
  newEndTime: string;
};

const DurationConfirmation = ({ onClose, newStartTime, newEndTime }: DurationConfirmationProps) => {
  const { booking, navigateTo, resetFlow } = useChangeBookingFlow();
  const updateBookingMutation = useUpdateBookingDateTime();
  const { member } = useMember();
  const [error, setError] = useState<string | null>(null);
  const [waive, setWaive] = useState(false);

  const isElevatedUser = useMemo(
    () => isMemberAnAdmin(member) || isMemberAPracticeManager(member) || isSystemAccount(member),
    [member],
  );

  // R16-style warning: an amendment within 24h of an occurrence still incurs the
  // original charge (members cannot shorten a 2hr booking to dodge the 2hr cost).
  // Calculated client-side for the UI hint only; the server re-checks authoritatively
  // and flags the document with wasChargedOnAmendment=true regardless.
  // NOTE: we do NOT pass isElevatedUser — we need the raw window check so elevated users
  // see the waive toggle (same pattern as CancelBookingDialogProvider's willBeCharged).
  const within24 = useMemo(
    () => (booking ? isBookingWithin24Hours(booking, {}) : { isWithin24Hours: false }),
    [booking],
  );

  // Chargeable amendment: within 24h of the occurrence AND the new duration is shorter
  // than the old duration. Mirrors the backend classification in PATCH /datetime.
  const amendmentIsChargeable = useMemo(() => {
    if (!within24.isWithin24Hours || !booking) return false;
    const toMinutes = (t: string) => {
      const padded = t.padStart(4, '0');
      return parseInt(padded.slice(0, 2), 10) * 60 + parseInt(padded.slice(2, 4), 10);
    };
    const oldDuration = endTimeToMinutes(booking.endTime, booking.startTime) - toMinutes(booking.startTime);
    const newDuration = endTimeToMinutes(newEndTime, newStartTime) - toMinutes(newStartTime);
    return newDuration < oldDuration;
  }, [within24.isWithin24Hours, booking, newStartTime, newEndTime]);

  const formatTime = (time: string) => {
    // Convert HHmm to HH:mm
    return `${time.slice(0, 2)}:${time.slice(2)}`;
  };

  const formatDuration = (startTime: string, endTime: string): string => {
    const startHour = Math.floor(parseInt(startTime.padStart(4, "0")) / 100);
    const startMinute = parseInt(startTime.padStart(4, "0")) % 100;
    const endHour = Math.floor(parseInt(endTime.padStart(4, "0")) / 100);
    const endMinute = parseInt(endTime.padStart(4, "0")) % 100;

    const durationMinutes = endHour * 60 + endMinute - (startHour * 60 + startMinute);
    const hours = Math.floor(durationMinutes / 60);
    const mins = durationMinutes % 60;

    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}hr`;
    } else {
      return `${hours}hr ${mins}m`;
    }
  };

  const handleConfirm = async () => {
    if (!booking) return;

    // Clear any previous errors
    setError(null);

    try {
      // Format the date to ISO string (YYYY-MM-DD)
      const formattedDate = format(booking.date, "yyyy-MM-dd");

      await updateBookingMutation.mutateAsync({
        bookingId: booking.id,
        newDate: formattedDate,
        newStartTime,
        newEndTime,
        ...(waive ? { waive: true } : {}),
      });

      console.log("Booking duration updated successfully");

      // Move to final confirmation screen
      navigateTo("confirmation");
    } catch (err) {
      console.error("Failed to update booking duration:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to update booking duration. Please try again.";
      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    resetFlow();
    onClose?.();
  };

  if (!booking) {
    return (
      <div className="p-6">
        <p className="text-red-600">Error: No booking data available</p>
        <Button onClick={handleCancel} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50">
        <h2 className="text-2xl font-semibold mb-2">Confirm Duration Change</h2>
        <p className="text-gray-600 text-xs">Review your changes and confirm to update your booking duration.</p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">Confirm Changes</h2>
          <p className="mb-2 text-gray-600 text-sm">
            Please review the changes below. Once confirmed, your booking duration will be updated.
          </p>
        </div>

        {/* Right column - Confirmation Details */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          {renderContent()}
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6">{renderContent()}</div>
    </div>
  );

  function renderContent() {
    return (
      <div className="flex flex-col gap-4">
        {/* API Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Update Failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Alert — hidden once an update has failed, so it never contradicts the error above. */}
        {!error && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <CheckCircle2 className="h-6 w-6 mt-1 text-green-800" />
            <AlertTitle className="text-green-800 text-lg">Ready to Update</AlertTitle>
            <AlertDescription className="text-green-800">
              Your booking duration can be safely updated to the new time.
            </AlertDescription>
          </Alert>
        )}

        {/* Current vs New Duration Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Current Duration */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Duration</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-gray-700" />
                <span className="text-gray-700">
                  {booking ? `${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}` : ""}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-700">Duration:</span>
                <span className="text-gray-700">
                  {booking ? formatDuration(booking.startTime, booking.endTime) : ""}
                </span>
              </div>
            </div>
          </div>

          {/* New Duration */}
          <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 text-indigo-800">New Duration</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-indigo-800" />
                <span className="text-indigo-900 font-medium">
                  {booking ? `${formatTime(newStartTime)} - ${formatTime(newEndTime)}` : ""}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-indigo-800">Duration:</span>
                <span className="text-indigo-800">{booking ? formatDuration(newStartTime, newEndTime) : ""}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Change Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-blue-800 mb-1">What's changing?</h4>
              {booking && newStartTime !== booking.startTime && (
                <div className="flex items-center gap-2 text-sm text-blue-800">
                  <span>Start time</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="font-medium">
                    from {formatTime(booking.startTime)} to {formatTime(newStartTime)}
                  </span>
                </div>
              )}
              {booking && newEndTime !== booking.endTime && (
                <div className="flex items-center gap-2 text-sm text-blue-800">
                  <span>End time</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="font-medium">
                    from {formatTime(booking.endTime)} to {formatTime(newEndTime)}
                  </span>
                </div>
              )}
              <p className="text-sm text-blue-800 mt-2">Your booking date will remain the same.</p>
            </div>
          </div>
        </div>

        {/* Within-24h charge notice — informational only, the user can still confirm. */}
        {within24.isWithin24Hours && (
          <div
            className="bg-amber-50 border border-amber-200 rounded-lg p-3"
            data-testid="duration-change-within-24h-warning"
          >
            <p className="text-sm text-amber-900">
              <strong>Heads up:</strong> This booking is within 24 hours. The original booking will still be charged
              even after this change — shortening or moving does not avoid the existing commitment.
              {waive && " — charge will be waived"}
            </p>
          </div>
        )}

        {/* Staff waive toggle — only for elevated users making a chargeable amendment (reduce). */}
        {isElevatedUser && amendmentIsChargeable && (
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              data-testid="edit-waive-checkbox"
              checked={waive}
              onCheckedChange={(v) => setWaive(Boolean(v))}
            />
            <span>Waive this charge (the member will not be charged).</span>
          </label>
        )}

        {/* Recurring Booking Note */}
        {booking && booking.recurrence && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-900">
              <strong>Note:</strong> This is a recurring booking. This change will apply to all future occurrences of
              this booking.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 mt-6">
          <Button
            className={`w-full ${defaultButtonClasses}`}
            onClick={handleConfirm}
            disabled={updateBookingMutation.isPending}
          >
            {updateBookingMutation.isPending ? (
              <>
                <Spinner className="size-4 mr-2" />
                Updating booking...
              </>
            ) : (
              "Confirm Change"
            )}
          </Button>
          <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }
};

export default DurationConfirmation;
